/**
 * submitSupportReport — assembles and submits a support report.
 *
 * Driver:
 *   1. Ask the plugin to assemble the diagnostic bundle (snapshot + versions).
 *   2. POST the report envelope to the gateway.
 *   3. Paginate diagnostic events from the plugin and append them to the report.
 *
 * @param {{
 *   message: string,
 *   contactEmail?: string,
 *   bridgeClient: import('../../../app/service/bridge/BridgeClient').BridgeClient,
 *   baseUrl: string,
 *   token: string,
 *   authToken: string,
 * }} options
 *
 * @returns {Promise<{ reportId: string }>}
 */
export async function submitSupportReport({
  message,
  contactEmail,
  bridgeClient,
  baseUrl,
  token,
  authToken,
}) {
  const base = baseUrl.replace(/\/+$/, '');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  };

  // ── Step 1: prepare bundle from plugin ──────────────────────────────────

  const bundleResponse = await bridgeClient.sendMessageWithResponse(
    'diagnostics.support.bundle_prepare',
    {},
  );

  if (bundleResponse.result === 'error') {
    throw new Error(bundleResponse.error || 'Failed to prepare support bundle');
  }

  const { snapshot, versions, generatedAt } = bundleResponse;

  // ── Step 2: create report on gateway ────────────────────────────────────

  const createUrl = `${base}/v1/installations/${encodeURIComponent(token)}/diagnostics/reports`;

  const createRes = await fetch(createUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({ snapshot, versions, message, contactEmail, generatedAt }),
  });

  if (!createRes.ok) {
    throw new Error(`Failed to create report: ${createRes.status} ${createRes.statusText}`);
  }

  const { reportId, lastSyncedEventId: initialSinceId } = await createRes.json();

  // ── Step 3: paginate and append events ──────────────────────────────────

  let sinceId = initialSinceId;
  let hasMore = true;

  while (hasMore) {
    const eventsResponse = await bridgeClient.sendMessageWithResponse(
      'diagnostics.events.list',
      { since_id: sinceId, limit: 100 },
    );

    if (eventsResponse.result === 'error') {
      // Non-fatal: stop pagination but keep the report
      break;
    }

    const events = eventsResponse.events || [];
    hasMore = !!eventsResponse.has_more;

    if (events.length > 0) {
      const eventsUrl = `${base}/v1/installations/${encodeURIComponent(token)}/diagnostics/reports/${encodeURIComponent(reportId)}/events`;

      const eventsRes = await fetch(eventsUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ sinceId, events }),
      });

      if (!eventsRes.ok) {
        // Non-fatal: stop pagination but keep the report
        break;
      }

      const eventsBody = await eventsRes.json();

      if (hasMore && eventsResponse.next_since_id != null) {
        sinceId = eventsResponse.next_since_id;
      } else {
        sinceId = eventsBody.lastSyncedEventId ?? sinceId;
      }
    } else {
      // No events in batch — stop to avoid infinite loop
      hasMore = false;
    }
  }

  return { reportId };
}
